import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Route, Switch } from 'react-router-dom';
import '../i18n'; // initialise i18next (side-effect import — must run before render)
import i18n from '../i18n';
import { LANG_STORAGE_KEY } from '../i18n';
import globals from '../services/globals';
import { hydrateLeaveTypes } from '../config/leaveTypes';
import { authFetch } from '../services/apiServices';
import helper from '../services/helper';
import Dashboard from '../components/dashboard/Dashboard';
import { TourProvider } from '../components/tour/TourProvider';
import KioskSetup from '../components/presence/KioskSetup';
import SignIn from '../components/auth/SignIn';
import PublicApplyForm from '../components/applications/PublicApplyForm';
import ResetPassword from '../components/auth/ResetPassword';
import SetPassword from '../components/auth/SetPassword';
import SsoCallback from '../components/auth/SsoCallback';
import { applySsoTokens } from '../services/ssoSession';
import IdleSecurityProvider from '../components/security/IdleSecurityProvider';
import ConsentPage from '../pages/ConsentPage';
import PublicOrgChart from '../pages/PublicOrgChart';
import PresenceMonitor from '../pages/PresenceMonitor';
import { clearStoragePreservingSeen } from '../utils/persistentStorage';
import { clearEffectivePermissions } from '../services/permissionService';
import SupportWidget from '../components/support/SupportWidget';
import ChatFab from '../components/chat/ChatFab';

// Faqja publike e vizitorëve. Ngarkohet bashkë me app-in që `/` të hapet pa
// pritje; Three.js i skenës 3D vjen veç, me `import()`, vetëm kur skena niset.
import PresentationPage from '../components/marketing/PresentationPage';

const hasLogoutIntent = () => {
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get('logout')) return true;

  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');
  if (queryIndex >= 0) {
    const hashParams = new URLSearchParams(hash.slice(queryIndex + 1));
    if (hashParams.get('logout')) return true;
  }

  return false;
};

export default function App() {
  const [user, setUser]       = useState(null);
  const [checked, setChecked] = useState(false);
  // Nëse ka sesion të ruajtur ose erdhi nga logout → shko direkt te login, anashkalo landing page.
  // Në app-in desktop (Electron) shfaq gjithmonë formën e login-it (email/ID/Google), jo landing-un.
  const [showLogin, setShowLogin] = useState(() => {
    if (hasLogoutIntent()) return true;
    if (typeof window !== 'undefined' && window.electronAPI) return true;
    return !!helper().getItem('logedInUser');
  });
  // Email-i që forma e kyçjes e merr të mbushur — p.sh. pasi kompania sapo u hap.
  const [loginEmail, setLoginEmail] = useState('');
  const openLogin = (email) => {
    setLoginEmail(typeof email === 'string' ? email : '');
    setShowLogin(true);
  };

  useEffect(() => {
    // Nëse erdhi nga logout, pastro gjithçka dhe mos ngarko sesionin
    const isLogout = hasLogoutIntent();
    if (isLogout) {
      try { sessionStorage.clear(); } catch {}
      clearStoragePreservingSeen();
      clearEffectivePermissions();  // drop cached RBAC perms so next login re-fetches
      try {
        document.cookie.split(';').forEach(c => {
          const key = c.trim().split('=')[0];
          document.cookie = key + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/';
          document.cookie = key + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        });
      } catch {}
      setUser(null);
      setShowLogin(true);
      if (window.location.search || (window.location.hash || '').includes('?logout=')) {
        window.history.replaceState({}, document.title, '/');
      }
      setChecked(true);
      return;
    }
    const u = globals().getLogedInUser();
    const isRealSession = u?.success && u?.data?.email;
    if (isRealSession) {
      setUser(u);
      // Apply stored language preference for this user
      try {
        const storedLang = localStorage.getItem(LANG_STORAGE_KEY);
        if (storedLang && storedLang !== i18n.language) {
          i18n.changeLanguage(storedLang);
        }
      } catch (_) {}
    }
    setChecked(true);
  }, []);

  /**
   * Llojet e pushimit lexohen nga baza sapo ka sesion.
   *
   * Lista e ngurtë te `config/leaveTypes.js` nuk përputhet me tabelën e vërtetë
   * — te prodhimi numri 4 është "Martese", jo "Leja e atësisë" — ndaj emrat që
   * shihte punonjësi nuk ishin ata që ruheshin. Kjo thirrje e vetme i ndreq të
   * nëntëmbëdhjetë vendet që i importojnë ato etiketa, sepse objektet
   * ndryshohen në vend.
   */
  useEffect(() => {
    if (!user) return;
    hydrateLeaveTypes(async () => {
      const res = await authFetch('/api/leaveTypes');
      const json = await res.json();
      return json?.data || [];
    });
  }, [user]);

  // Electron desktop deep-link: the main process forwards nexuma://auth/callback
  // tokens via IPC. Store the same session and enter the app. No-op on the web.
  useEffect(() => {
    const api = typeof window !== 'undefined' ? window.electronAPI : null;
    if (!api?.onOAuthCallback) return;
    api.onOAuthCallback(async ({ token, refresh, error } = {}) => {
      // Backend rejected the login (no auto-create) — show the reason via the callback route.
      if (error) { window.location.hash = `#/auth/callback?error=${encodeURIComponent(error)}`; return; }
      try {
        const ok = await applySsoTokens(token, refresh);
        if (ok) window.location.replace('/');
      } catch (_) { /* stay on login */ }
    });
  }, []);

  if (!checked) return null;

  return (
    <Router>
      <Switch>
        {/* Kiosk (regjistrim pajisjeje) — nuk kërkon login */}
        <Route path="/kiosk" component={KioskSetup} />

        {/* /scan NUK është më publike: QR-ja e prezencës është kredencial, dhe
            pa login backend-i i jepte këdo QR-në e çdo punëtori (check-in si
            dikush tjetër). Rrugët /scan të kyçura jepen nga Dashboard-i, dhe
            WorkerScanScreen tashmë e lexonte sesionin si rrugë kryesore. */}

        {/* Aplikim publik — nuk kërkon login */}
        <Route path="/apply/:token" component={PublicApplyForm} />

        {/* Pajtimi i vendimit të ndërprerjes — publik, pa login */}
        <Route path="/consent/:token" component={ConsentPage} />

        {/* Organigrami publik — pa login (për monitor/recepsion) */}
        <Route path="/public/org-chart/:token" component={PublicOrgChart} />

        {/* Reset/Set password — publike, pa login */}
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/set-password" component={SetPassword} />

        {/* Monitoring standalone window — pa login (auth via cookie/token bridge) */}
        <Route path="/monitoring" component={PresenceMonitor} />

        {/* Google SSO callback — landon tokenat dhe ndërton sesionin */}
        <Route path="/auth/callback" component={SsoCallback} />

        {/* Faqja prezantuese edhe me sesion të hapur (p.sh. për ta treguar dikujt).
            «Hyr» çon te `/`: paneli kur ka sesion, përndryshe forma e kyçjes. */}
        <Route
          path="/prezantimi"
          render={({ history }) => (
            <PresentationPage basePath="#/prezantimi" onLogin={(email) => { if (!user) openLogin(email); history.push('/'); }} />
          )}
        />

        {user
          ? <Route path="/" render={() => <IdleSecurityProvider><TourProvider><Dashboard /></TourProvider></IdleSecurityProvider>} />
          : (
            <Route path="/">
              {showLogin
                ? <SignIn initialEmail={loginEmail} onBack={() => setShowLogin(false)} />
                : <PresentationPage onLogin={openLogin} />
              }
            </Route>
          )
        }
      </Switch>

      {/*
        Kutia e ndihmës rri JASHTË `Switch`-it, që të jetë aty te çdo faqe —
        edhe te hyrja, ku duhet më shumë se kudo tjetër: pikërisht ai që s'hyn
        dot është njeriu që ka nevojë të shkruajë, dhe ai nuk kalon kurrë te
        faqet e brendshme për ta gjetur.
      */}
      {/*
        Inside the app the bottom-right slot belongs to chat — it is opened far
        more often — and support moves into the sidebar ("Ndihmë", last entry).

        Signed out there is no sidebar and no chat, so the support launcher
        takes the slot back: that is exactly the case the note above is about.
      */}
      {/*
        Te kioska (#/kiosk) këndi poshtë djathtas i takon konfigurimit: aty
        rrinë ikona ⚙ dhe zona e 5 prekjeve. «Ndihmë» rrinte sipër tyre, ndaj
        prekjet hapnin ndihmën — dhe tableti i FAR Logistics s'u konfigurua dot.
      */}
      <Route render={({ location }) => (location.pathname.startsWith('/kiosk') ? null : <>
        <ChatFab />
        <SupportWidget showLauncher={!user} />
      </>)} />
    </Router>
  );
}
